import api from './api';
import partnerMktSettingsService from './partnerMktSettingsService';

// VENDORS — companies with Universal API credentials.
//
// SERVED BY THE MAIN API (/uapi-vendors), not the admin one. A partner's vendors are the partner's
// business, and routing this through /admin/* would have meant every addition was a support request.
// The lambda resolves the partner from the CALLER, so a partner admin sees their own and a superadmin
// sees whichever they ask for — the path is the same either way.
//
// READING is its own endpoint, because "every vendor across every partner, with what the queue says
// about each" is one query on the server and a dozen round trips from here.
//
// CREATING is NOT. A vendor's credential is minted and stored exactly the way the access wizard does it
// — generate the app client, then save it through the ordinary credential write — so there is one write
// path for a credential on this platform and one place that knows which settings a marketplace defines.
// The only difference is the entity it is held against: a partner, not a hotel, which is what makes it
// reach across that partner's chains.

// The settings the generate step mints, by the marketplace setting they belong in. Only names the
// marketplace actually defines are written — same rule as the wizard, for the same reason: writing a
// setting the marketplace does not define stores a value no form will ever render.
// THE SETTINGS THAT MAKE UP A UNIVERSAL API CREDENTIAL, named once and exported, so the screen showing
// them and the code writing them cannot disagree about which ones they are. A credential may carry other
// settings the marketplace defines; these are the ones generating issues.
export const UAPI_CREDENTIAL_SETTINGS = [
    'CLIENTID', 'CLIENTSECRET', 'TOKENURL', 'SCOPE', 'APPKEY', 'ENTERPRISEID', 'EXTERNALSYSTEM',
];

const GENERATED_SETTINGS = (issued) => ({
    CLIENTID: issued.clientId,
    CLIENTSECRET: issued.clientSecret,
    TOKENURL: issued.tokenUrl,
    SCOPE: issued.scope,
    APPKEY: issued.appKey,
    ENTERPRISEID: issued.enterpriseId,
    EXTERNALSYSTEM: issued.externalSystem,
});

const uapiVendorsService = {
    list: async (params = {}) => {
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) if (v) qs.set(k, String(v));
        const response = await api.get(`/uapi-vendors${qs.toString() ? `?${qs}` : ''}`);
        return response.data;
    },

    get: async (id) => {
        const response = await api.get(`/uapi-vendors/${id}`);
        return response.data;
    },

    /**
     * Remove a vendor: its credential, its profile and everything hanging off the credential.
     *
     * A HARD DELETE. The credential IS the vendor — a soft-deleted one would be a live client id and
     * secret that the screen no longer shows, which is the opposite of what deleting it is for. The
     * delivery history survives on purpose; the server's note says what goes and what stays.
     */
    remove: async (id) => {
        const response = await api.delete(`/uapi-vendors/${id}`);
        return response.data;
    },

    // Rename the vendor — its credential, and the copies of the name the credential screens show. 409 when the
    // partner already has a credential by that name.
    rename: async (id, name) => {
        const response = await api.put(`/uapi-vendors/${id}`, { name });
        return response.data;
    },

    // ONE PERSON AT THE VENDOR: their role there ('A' admin / 'U' user), or removing them from it — which also takes away
    // the chain / hotel grants they had through the vendor. Their account stays.
    setUserRole: async (id, userId, role) => {
        const response = await api.put(`/uapi-vendors/${id}/users/${userId}`, { role });
        return response.data;
    },
    removeUser: async (id, userId) => {
        const response = await api.delete(`/uapi-vendors/${id}/users/${userId}`);
        return response.data;
    },

    // THE CHAINS AND HOTELS anyone logging in as this vendor may reach, and the choices available.
    // Both come back together: the screen needs what is granted and what could be, and they are one
    // query each against the same partner.
    getAccess: async (id) => {
        const response = await api.get(`/uapi-vendors/${id}/access`);
        return response.data;
    },

    // THE WHOLE SET, applied as a diff. The screen sends the set it wants and the set it LOADED (`expected`); the
    // server changes only what differs and refuses (409) if somebody else changed the grants in between (HIL-126).
    saveAccess: async (id, grants, expected = null) => {
        const response = await api.put(`/uapi-vendors/${id}/access`, { grants, ...(expected ? { expected } : {}) });
        return response.data;
    },

    // GRANT ONE PROPERTY TO A VENDOR, idempotently (uapi_vendor_access, level 'U'). The access list is held
    // wholesale, so this reads it, adds the hotel only when neither it nor its chain is already granted, and writes
    // it back with the list it read as the optimistic-lock expectation — the same read/append/save the vendor screen
    // does by hand. Used where a grant must happen as a SIDE EFFECT of another action — the pipeline board issuing a
    // direct-UAPI vendor's x-hotelid — so the two-step vendor screen is not the only way to add a property.
    // Returns { granted: true } when it added one, { granted: false } when the vendor already covered the property.
    grantHotel: async (id, hotelId, accountId = null) => {
        const acc = await api.get(`/uapi-vendors/${id}/access`).then(r => r.data);
        const loaded = (acc?.grants || []).map(g => ({
            accountId: g.account_id || null,
            hotelId: g.hotel_id || null,
            permissionType: ['A', 'U'].includes(g.permission_type) ? g.permission_type : 'N',
        }));
        const covered = loaded.some(g => String(g.hotelId) === String(hotelId))
            || (accountId != null && loaded.some(g => g.accountId && String(g.accountId) === String(accountId)));
        if (covered) return { granted: false };
        const grants = [...loaded, { accountId: null, hotelId: Number(hotelId), permissionType: 'U' }];
        await api.put(`/uapi-vendors/${id}/access`, { grants, expected: loaded });
        return { granted: true };
    },

    // WHAT THE VENDOR MAY CALL — the module grants on its credential (uapi_credential_module, V420).
    // The grantable routes come back with the grants, so the picker can never offer one the server
    // would reject, and the un-revokable path (/oauth, the token endpoint) is NAMED by the server
    // rather than hardcoded here.
    getModules: async (id) => {
        const response = await api.get(`/uapi-vendors/${id}/modules`);
        return response.data;
    },

    // WHOLESALE, like the chains and hotels: the screen sends every module it shows as ticked, so an
    // unticked one is revoked. /oauth is preserved by the server whether or not it is in the list.
    saveModules: async (id, uapiRouteIds) => {
        const response = await api.put(`/uapi-vendors/${id}/modules`, { uapiRouteIds });
        return response.data;
    },

    // WHAT IT CAN ACTUALLY CALL, AND WHERE — the Test tab's whole starting position.
    //
    // The operations of the modules granted to this vendor (narrowed to the named ones on a SELECTED
    // grant) plus the property bindings issued to its credential, which is what the gateway checks the
    // x-hotelid against. Not read from /admin/legacy-uapi/*: that surface is superadmin's, and this
    // screen belongs to the partner as much as to us.
    getTestSurface: async (id) => {
        const response = await api.get(`/uapi-vendors/${id}/test`);
        return response.data;
    },

    /**
     * Create a vendor: the company, and nothing else.
     *
     * NO CREDENTIALS ARE ISSUED HERE. This used to mint a Cognito app client and write the client id and
     * secret in the same breath, which meant the one moment the secret is visible arrived before anybody
     * had decided the vendor was even set up correctly — and a failure anywhere after it left a live
     * secret that had been shown once and recorded nowhere.
     *
     * A vendor is now created empty and given credentials from its own screen, when somebody is ready to
     * hand them over. The record is an entity_credentials row at PARTNER level for the chosen system —
     * the identity — with no entity_marketplace_settings behind it yet.
     *
     * Written through the ordinary credential save (`settings: []`) rather than a create of its own, so
     * there is still exactly one write path for a credential on this platform.
     */
    create: async ({ name, partnerId, marketplaceId }) => {
        if (!name || !partnerId) throw new Error('a vendor needs a name and a partner');
        if (!marketplaceId) throw new Error('choose the system these credentials are for');

        const saved = await partnerMktSettingsService.saveCredentials(
            'partner', partnerId, marketplaceId, name, [], undefined, true,   // isVendor: this IS the vendor credential (V715)
        );
        return { credentialId: saved?.credentialId || null };
    },

    /**
     * Issue this vendor's credentials — the app client, saved onto the credential that already exists.
     *
     * Returns `issued`, which carries the CLIENT SECRET. It exists in this response and nowhere readable
     * afterwards; the caller must show it once and say so.
     */
    issueCredentials: async ({ credentialId, name, partnerId, marketplaceId }) => {
        // NAME WHAT IS MISSING. This used to say "the vendor must exist before credentials can be issued
        // for it", which is a confusing thing to read on a page that is plainly showing the vendor — the
        // vendor existed, one value the call needs had not reached the screen. Say which.
        const missing = [
            !credentialId && 'the vendor id',
            !partnerId && 'its partner',
            !marketplaceId && 'its system',
        ].filter(Boolean);
        if (missing.length) {
            throw new Error(`Cannot issue credentials — ${missing.join(' and ')} `
                + `${missing.length === 1 ? 'is' : 'are'} missing from this vendor. `
                + 'Reload the page; if it persists the vendor record is incomplete.');
        }
        const genRes = await partnerMktSettingsService.generateUapiCredentials({ credentialName: name });
        const issued = genRes.data || genRes;

        // EVERY GENERATED VALUE IS WRITTEN, whatever the marketplace defines.
        //
        // This used to intersect the generated values with the marketplace's setting schema, on the rule
        // that writing a setting a marketplace does not define stores a value no form will ever render.
        // That rule is right for a credential belonging to the VENDOR'S system — and wrong here, because
        // these five are OUR contract, not theirs: POST /oauth/v1/tokens refuses a credential unless
        // CLIENTID, CLIENTSECRET, APPKEY, EXTERNALSYSTEM and ENTERPRISEID are all present AND match.
        //
        // Thynk's marketplace defines only CLIENTID and CLIENTSECRET, so the intersection wrote two of
        // the five and the credential could never log in — a vendor handed credentials that were
        // refused by the endpoint they were issued for, with nothing on the screen to say why.
        const values = GENERATED_SETTINGS(issued);
        const settings = Object.entries(values)
            .filter(([, v]) => v !== undefined && v !== null && String(v) !== '')
            .map(([name, value]) => ({ name, value }));

        if (!settings.length) {
            throw new Error('The credential generator returned no values — nothing has been saved. '
                + 'Try again, and if it persists this environment cannot mint app clients.');
        }

        // The SAME credential row, updated — `credentialId` is what makes this an edit rather than a
        // second credential beside the first.
        await partnerMktSettingsService.saveCredentials(
            'partner', partnerId, marketplaceId, name, settings, credentialId,
        );
        return { issued };
    },
};

export default uapiVendorsService;
